import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaExternalLinkAlt, FaFileInvoiceDollar, FaFilePdf, FaTrash } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { getModule } from '../../components/DeclaracionRenta/F22/registry';
import { getRenta, getRentaPagado, eliminarPdfPagado } from '../../services/declaracionRenta/f22Service';
import useCargarCertificadoPagado from '../../hooks/useCargarCertificadoPagado';
import { useAuth } from '../../context/AuthContext';
import '../../styles/pages/ServiciosSII/DeclaracionRentaPage.css';
// Reusa .renta-saved-btn-* (botones de acción) y .f22-loading/.f22-spinner.
import '../../styles/components/F22Form.css';
import '../../styles/pages/ServiciosSII/CertificadoRentaPagadaPage.css';

const formatCLP = (valor) =>
  valor === null || valor === undefined || valor === '' ? '—' : Number(valor).toLocaleString('es-CL');

const labelRegimen = (regimen) => (regimen === '14d3' ? 'Pro PYME (14D3)' : String(regimen).toUpperCase());

const tieneValor = (valor) => valor !== null && valor !== undefined && valor !== '';

/**
 * Valor de un código del certificado: casi todos son montos, pero no todos
 * (cod_315 es la fecha de presentación, que se guarda y se muestra como texto).
 * Se decide por el valor y no por una lista de códigos especiales.
 */
const formatCodigo = (valor) => {
  if (!tieneValor(valor)) return '—';
  return Number.isFinite(Number(valor)) ? formatCLP(valor) : String(valor);
};

/**
 * Página del Certificado de Renta Pagada: todo lo relativo al F-22 que la
 * empresa presentó ante el SII para un (año, régimen) — la ficha del PDF
 * cargado, los códigos que se le extrajeron, la comparación contra lo que
 * calculó el sistema, y las acciones de reemplazar y eliminar.
 *
 * Se carga sola desde la URL (no depende del wizard), así funciona con link
 * directo y al refrescar. Los dos lados son independientes: puede existir el
 * certificado sin cálculo (empresa que parte en el año X y trae su balance
 * por asientos manuales), el cálculo sin certificado, o ambos.
 */
const CertificadoRentaPagadaPage = () => {
  const { anio, regimen } = useParams();
  const anioTributario = Number(anio);
  const navigate = useNavigate();
  const { selectedCompany } = useAuth();
  const idCompany = selectedCompany?.id_company || selectedCompany?.id;

  const mod = useMemo(() => getModule(anioTributario, regimen), [anioTributario, regimen]);
  const catalogo = useMemo(() => mod?.CODIGOS_PAGADO || [], [mod]);
  const destacados = useMemo(() => mod?.CODIGOS_DESTACADOS || [], [mod]);
  const outputCodes = useMemo(() => mod?.OUTPUT_CODES || [], [mod]);

  const [loading, setLoading] = useState(true);
  const [pagado, setPagado] = useState(null);
  const [calculado, setCalculado] = useState(null);
  const [eliminando, setEliminando] = useState(false);

  const cargar = useCallback(async () => {
    if (!idCompany || !mod) {
      setLoading(false);
      return;
    }
    setLoading(true);
    // Los dos endpoints responden 404 cuando no hay nada guardado: eso no es
    // un error, es uno de los estados válidos de esta página.
    const [resCalculado, resPagado] = await Promise.all([
      getRenta(idCompany, anioTributario, regimen).catch(() => null),
      getRentaPagado(idCompany, anioTributario, regimen).catch(() => null),
    ]);
    setCalculado(resCalculado?.success ? resCalculado.data : null);
    setPagado(resPagado?.success ? resPagado.data : null);
    setLoading(false);
  }, [idCompany, mod, anioTributario, regimen]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const { cargando, seleccionarArchivo, inputProps } = useCargarCertificadoPagado(anioTributario, regimen, cargar);

  const extraidos = useMemo(
    () => (pagado ? catalogo.filter((c) => tieneValor(pagado[c.column])).length : 0),
    [pagado, catalogo]
  );

  /**
   * Cruce entre lo calculado y lo declarado. Solo son comparables los códigos
   * que existen en AMBOS lados: el cálculo produce varios que el certificado
   * no trae, y mostrarlos como diferencias sería falso — van aparte.
   */
  const comparacion = useMemo(() => {
    if (!pagado || !calculado) return null;

    const enCertificado = new Map(catalogo.map((c) => [c.column, c]));
    const filas = [];
    const sinEquivalente = [];

    for (const { column } of outputCodes) {
      const entrada = enCertificado.get(column);
      if (!entrada) {
        sinEquivalente.push({ column, codigo: column.replace('cod_', ''), valor: calculado[column] });
        continue;
      }
      const valorCalculado = Number(calculado[column] ?? 0);
      const valorDeclarado = tieneValor(pagado[column]) ? Number(pagado[column]) : null;
      filas.push({
        ...entrada,
        calculado: valorCalculado,
        declarado: valorDeclarado,
        diferencia: valorDeclarado === null ? null : valorCalculado - valorDeclarado,
        coincide: valorDeclarado !== null && valorCalculado === valorDeclarado,
      });
    }

    const comparables = filas.filter((f) => f.declarado !== null);
    return {
      filas,
      sinEquivalente,
      total: comparables.length,
      coinciden: comparables.filter((f) => f.coincide).length,
    };
  }, [pagado, calculado, catalogo, outputCodes]);

  const handleEliminar = async () => {
    const confirmacion = await Swal.fire({
      title: '¿Eliminar el certificado?',
      html: 'Se eliminará el PDF cargado y los códigos que se extrajeron de él. Después podrás cargar otro.<br/><br/>La declaración calculada en el sistema <strong>no se toca</strong>. Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });
    if (!confirmacion.isConfirmed) return;

    setEliminando(true);
    try {
      const result = await eliminarPdfPagado(idCompany, anioTributario, regimen);
      if (result.warnings?.length) {
        await Swal.fire('Eliminado con avisos', result.warnings.join('<br/>'), 'warning');
      } else {
        await Swal.fire('Listo', 'El certificado fue eliminado.', 'success');
      }
      await cargar();
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'No se pudo eliminar el certificado', 'error');
    } finally {
      setEliminando(false);
    }
  };

  const renderContenido = () => {
    if (!mod) {
      return (
        <div className="cert-vacio">
          <p>No hay un módulo de Declaración de Renta para AT {anio} / {labelRegimen(regimen)}.</p>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="f22-loading">
          <div className="f22-spinner" />
          <p>Cargando certificado...</p>
        </div>
      );
    }

    if (!pagado) {
      return (
        <div className="cert-vacio">
          <FaFilePdf className="cert-vacio-icono" />
          <h2>Aún no hay certificado cargado</h2>
          <p>
            Esta empresa todavía no tiene registrado su Formulario 22 presentado ante el SII
            para el AT {anio}. Cárgalo para dejar constancia de lo efectivamente declarado y pagado.
          </p>
          <p className="cert-vacio-nota">
            No necesitas haber calculado la renta de este año en el sistema: el certificado se
            guarda igual y sirve como respaldo. Si después la calculas, la comparación aparece
            aquí sola.
          </p>
          <button className="renta-saved-btn renta-saved-btn-secondary" onClick={seleccionarArchivo} disabled={cargando}>
            <FaFilePdf /> {cargando ? 'Procesando...' : 'Cargar PDF Renta Pagada'}
          </button>
        </div>
      );
    }

    return (
      <>
        <div className="cert-ficha">
          <div className="cert-ficha-datos">
            <div className="cert-dato">
              <span className="cert-dato-label">Año tributario</span>
              <span className="cert-dato-valor">{pagado.anio_tributario || anio}</span>
            </div>
            <div className="cert-dato">
              <span className="cert-dato-label">Régimen</span>
              <span className="cert-dato-valor">{labelRegimen(pagado.regimen || regimen)}</span>
            </div>
            <div className="cert-dato">
              <span className="cert-dato-label">Fecha de presentación</span>
              <span className="cert-dato-valor">{formatCodigo(pagado.cod_315)}</span>
            </div>
            <div className="cert-dato">
              <span className="cert-dato-label">Códigos extraídos</span>
              <span className="cert-dato-valor">{extraidos} de {catalogo.length}</span>
            </div>
          </div>

          <div className="cert-acciones">
            {pagado.pdf_url && (
              <a
                href={pagado.pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                className="renta-saved-btn renta-saved-btn-primary"
              >
                <FaExternalLinkAlt /> Ver PDF
              </a>
            )}
            <button className="renta-saved-btn renta-saved-btn-danger" onClick={handleEliminar} disabled={eliminando}>
              <FaTrash /> {eliminando ? 'Eliminando...' : 'Eliminar certificado'}
            </button>
            <p className="cert-nota-acciones">
              Para cargar otro certificado debes eliminar primero el actual.
            </p>
          </div>
        </div>

        {destacados.length > 0 && (
          <div className="cert-destacados">
            {destacados.map(({ column, etiqueta }) => (
              <div key={column} className="cert-destacado">
                <span className="cert-destacado-codigo">Código {column.replace('cod_', '')}</span>
                <span className="cert-destacado-etiqueta">{etiqueta}</span>
                <span className="cert-destacado-valor">{formatCLP(pagado[column])}</span>
              </div>
            ))}
          </div>
        )}

        <section className="cert-seccion">
          <div className="cert-seccion-header">
            <h2>Comparación con lo calculado</h2>
            {comparacion && (
              <span className={`cert-badge ${comparacion.coinciden === comparacion.total ? 'ok' : 'alerta'}`}>
                {comparacion.coinciden} de {comparacion.total} coinciden
              </span>
            )}
          </div>

          {!comparacion ? (
            <p className="cert-aviso">
              Aún no has calculado la Declaración de Renta AT {anio} en el sistema, así que no hay
              nada contra qué comparar todavía. El certificado queda registrado como respaldo; si
              más adelante calculas este año, la comparación aparecerá acá automáticamente.
            </p>
          ) : (
            <>
              <div className="cert-tabla-container">
                <table className="cert-tabla">
                  <thead>
                    <tr>
                      <th className="col-codigo">Código</th>
                      <th>Descripción</th>
                      <th className="col-valor">Calculado</th>
                      <th className="col-valor">Declarado</th>
                      <th className="col-valor">Diferencia</th>
                      <th className="col-estado">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparacion.filas.map((fila) => (
                      <tr
                        key={fila.column}
                        className={fila.declarado === null ? 'fila-sin-dato' : fila.coincide ? '' : 'fila-difiere'}
                      >
                        <td className="col-codigo">{fila.codigo}</td>
                        <td>{fila.nombre}</td>
                        <td className="col-valor">{formatCLP(fila.calculado)}</td>
                        <td className="col-valor">{formatCLP(fila.declarado)}</td>
                        <td className="col-valor">
                          {fila.diferencia === null ? '—' : formatCLP(fila.diferencia)}
                        </td>
                        <td className="col-estado">
                          {fila.declarado === null ? (
                            <span className="cert-chip neutro">No extraído</span>
                          ) : fila.coincide ? (
                            <span className="cert-chip ok">Coincide</span>
                          ) : (
                            <span className="cert-chip alerta">Difiere</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {comparacion.sinEquivalente.length > 0 && (
                <details className="cert-detalle">
                  <summary>
                    {comparacion.sinEquivalente.length} códigos calculados que el certificado no incluye
                  </summary>
                  <p className="cert-aviso">
                    El sistema los calcula, pero no forman parte de los códigos que se extraen del
                    F-22, así que no hay contra qué contrastarlos.
                  </p>
                  <div className="cert-tabla-container">
                    <table className="cert-tabla">
                      <thead>
                        <tr>
                          <th className="col-codigo">Código</th>
                          <th className="col-valor">Calculado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comparacion.sinEquivalente.map((fila) => (
                          <tr key={fila.column}>
                            <td className="col-codigo">{fila.codigo}</td>
                            <td className="col-valor">{formatCLP(fila.valor)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
            </>
          )}
        </section>

        <section className="cert-seccion">
          <div className="cert-seccion-header">
            <h2>Códigos del certificado</h2>
            <span className="cert-badge neutro">{extraidos} con valor</span>
          </div>
          <div className="cert-tabla-container">
            <table className="cert-tabla">
              <thead>
                <tr>
                  <th className="col-codigo">Código</th>
                  <th>Descripción</th>
                  <th className="col-valor">Valor (CLP)</th>
                </tr>
              </thead>
              <tbody>
                {catalogo.map(({ column, codigo, nombre }) => (
                  <tr key={column} className={tieneValor(pagado[column]) ? '' : 'fila-sin-dato'}>
                    <td className="col-codigo">{codigo}</td>
                    <td>{nombre}</td>
                    <td className="col-valor">{formatCodigo(pagado[column])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </>
    );
  };

  return (
    <div className="declaracion-renta-page">
      <div className="page-header">
        <div className="header-content">
          <div className="title-section">
            <FaFileInvoiceDollar className="page-icon" />
            <div>
              <h1>Certificado Renta Pagada AT {anio}</h1>
              <p className="page-subtitle">
                Formulario 22 presentado ante el SII · {labelRegimen(regimen)}
              </p>
            </div>
          </div>
          <button className="dr-btn-volver" onClick={() => navigate('/declaracion-renta')}>
            <FaArrowLeft /> Volver a Declaración de Renta
          </button>
        </div>
      </div>

      <div className="cert-contenido">{renderContenido()}</div>

      <input {...inputProps} />
    </div>
  );
};

export default CertificadoRentaPagadaPage;
